<!-- Trade attempts chart section -->
<div class="trade-attempt-section">

    <!-- Section header: title always visible; tabs hidden while loading -->
    <div class="section-header" [ngClass]="{ 'border-bottom': allSelected || error || loading || noTradeRecords }">
        <h3 class="section-title sdps-font-weight-bold">{{ sharedConstant.tradeChart.sectionTitle }}</h3>
        <div class="tabs" role="tablist" *ngIf="!loading && !allSelected && !error">
            <button *ngFor="let tab of normalizedTabs" [attr.id]="'trade-attempts-tab-' + tab[0]"
                [class.active]="activeTab === tab[0]" (click)="onTabChange(tab[0])" role="tab"
                [attr.aria-selected]="activeTab === tab[0]" aria-controls="trade-attempts-panel"
                [attr.tabindex]="activeTab === tab[0] ? 0 : -1">
                {{ tab[1] }}
            </button>
        </div>
    </div>

    <!-- Loading spinner -->
    <div class="trade-chart-spinner trade-chart-container" *ngIf="loading && !error && !allSelected" role="status"
        aria-label="Loading trade attempts chart">
        <span class="sch sch-spinner sch-spin-stepped"></span>
    </div>

    <!-- Empty state — shown when All is selected (orgId = 0) -->
    <div class="empty-state" *ngIf="allSelected">
        <div class="kpi-row">
            <div class="kpi-card" *ngFor="let card of emptyKpiCards">
                <span class="kpi-label">{{ card.label }}</span>
                <div class="kpi-main">
                    <span class="kpi-count"></span>
                    <span>__</span>
                </div>
                <span class="kpi-subtitle">{{ card.subtitle }}</span>
            </div>
        </div>
        <div class="trade-chart-container allSelection">
            <svg width="100" height="70" viewBox="0 0 100 70" aria-hidden="true">
                <rect x="8" y="35" width="10" height="30" rx="2" />
                <rect x="22" y="20" width="10" height="45" rx="2" />
                <rect x="36" y="28" width="10" height="37" rx="2" />
                <rect x="50" y="15" width="10" height="50" rx="2" />
                <rect x="64" y="25" width="10" height="40" rx="2" />
                <rect x="78" y="32" width="10" height="33" rx="2" />
            </svg>
            <h4>{{ sharedConstant.tradeChart.emptyState.heading }}</h4>
            <p>{{ sharedConstant.tradeChart.emptyState.subtext }}</p>
        </div>
    </div>

    <!-- Error state — trade attempt API failed -->
    <div class="trade-chart-container error-state" *ngIf="!loading && error && !allSelected" role="alert">
        <div class="state-icon-wrapper">
            <svg width="32" height="32" viewBox="0 0 24 24" aria-hidden="true">
                <path
                    d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
            </svg>
        </div>
        <h4>{{ sharedConstant.tradeChart.errorState.heading }}</h4>
        <p>{{ sharedConstant.tradeChart.errorState.subtext }}</p>
    </div>

    <!-- KPI cards + chart — shown when data is available -->
    <div *ngIf="data && !loading && !allSelected && !error"
        id="trade-attempts-panel"
        role="tabpanel"
        [attr.aria-labelledby]="'trade-attempts-tab-' + activeTab">

        <!-- KPI cards — 4 cards, each with color-coded top border -->
        <div class="kpi-row" *ngIf="activeKpi && !noTradeRecords">

            <div class="kpi-card kpi-total">
                <span class="kpi-label">{{ sharedConstant.tradeChart.kpiLabels.totalAttempts }}</span>
                <div class="kpi-main">
                    <span class="kpi-count">{{ activeKpi.tradeAttemptsTotal.count | number }}</span>
                    <span class="kpi-diff" [class.positive]="isPositive(activeKpi.tradeAttemptsTotal.difference)"
                        [class.negative]="isNegative(activeKpi.tradeAttemptsTotal.difference)">
                        {{ formatDifference(activeKpi.tradeAttemptsTotal.difference) }}
                    </span>
                </div>
                <span class="kpi-subtitle">{{ sharedConstant.tradeChart.kpiSubtitles.vsPreviousPeriod }}</span>
            </div>

            <div class="kpi-card kpi-allowed">
                <span class="kpi-label">{{ sharedConstant.tradeChart.kpiLabels.allowed }}</span>
                <div class="kpi-main">
                    <span class="kpi-count">{{ activeKpi.tradeAttemptsAllowed.count | number }}</span>
                    <span class="kpi-diff" [class.positive]="isPositive(activeKpi.tradeAttemptsAllowed.difference)"
                        [class.negative]="isNegative(activeKpi.tradeAttemptsAllowed.difference)">
                        {{ formatDifference(activeKpi.tradeAttemptsAllowed.difference) }}
                    </span>
                </div>
                <span class="kpi-subtitle" *ngIf="activeKpi.tradeAttemptsAllowed.totalPercentage">
                    {{ activeKpi.tradeAttemptsAllowed.totalPercentage }}{{
                    sharedConstant.tradeChart.kpiSubtitles.percentOfTotal }}
                </span>
            </div>

            <div class="kpi-card kpi-restricted">
                <span class="kpi-label">{{ sharedConstant.tradeChart.kpiLabels.restricted }}</span>
                <div class="kpi-main">
                    <span class="kpi-count">{{ activeKpi.tradeAttemptsRestricted.count | number }}</span>
                    <span class="kpi-diff" [class.positive]="isPositive(activeKpi.tradeAttemptsRestricted.difference)"
                        [class.negative]="isNegative(activeKpi.tradeAttemptsRestricted.difference)">
                        {{ formatDifference(activeKpi.tradeAttemptsRestricted.difference) }}
                    </span>
                </div>
                <span class="kpi-subtitle" *ngIf="activeKpi.tradeAttemptsRestricted.totalPercentage">
                    {{ activeKpi.tradeAttemptsRestricted.totalPercentage }}{{
                    sharedConstant.tradeChart.kpiSubtitles.percentOfTotal }}
                </span>
            </div>

            <div class="kpi-card kpi-blockrate">
                <span class="kpi-label">{{ sharedConstant.tradeChart.kpiLabels.blockRate }}</span>
                <div class="kpi-main">
                    <span class="kpi-count">{{ formatPercent(activeKpi.blockRate) }}</span>
                </div>
                <span class="kpi-subtitle">{{ sharedConstant.tradeChart.kpiSubtitles.vsPreviousPeriod }}</span>
            </div>

        </div>

        <!-- ECharts line chart -->
        <div echarts [options]="chartOptions" class="trade-chart-container" role="img"
            aria-label="Trade attempts over time line chart" *ngIf="!noTradeRecords">
        </div>

        <!-- No-data state — API returned successfully but no points for the selected period -->
        <div class="trade-chart-container no-data-state" *ngIf="noTradeRecords" role="status">
            <div class="state-icon-wrapper">
                <svg width="32" height="32" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 5h2v2h-2zm0 4h2v6h-2z"/>
                </svg>
            </div>
            <h4>{{ sharedConstant.tradeChart.noDataState.heading }}</h4>
            <p>{{ sharedConstant.tradeChart.noDataState.subtext }}</p>
        </div>

        <!-- Footer: legend + download button -->
        <div class="chart-footer" *ngIf="!noTradeRecords">
            <div class="legend">
                <span class="legend-item">
                    <span class="legend-dot legend-dot--allowed"></span>{{ sharedConstant.tradeChart.legend.allowed }}
                </span>
                <span class="legend-item">
                    <span class="legend-dot legend-dot--restricted"></span>{{
                    sharedConstant.tradeChart.legend.restricted }}
                </span>
                <span class="legend-item legend-type">&#8599; {{ sharedConstant.tradeChart.legend.lineArea }}</span>
            </div>
            <button class="download-btn buttons" (click)="onDownloadReport()"
                aria-label="Download trade attempt report">
                <span class="sch-download"></span> {{ sharedConstant.tradeChart.footer.downloadReport }}
            </button>
        </div>

    </div>

</div>